import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Divider,
  Form,
  Grid,
  Input,
  Modal,
  Space,
  Switch,
  Table,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import api from '../../../api/client';

const { Title } = Typography;

const Clientes = () => {
  const screens = Grid.useBreakpoint();
  const isNarrow = screens.md === false;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/clientes', {
        params: { page, limit, search: searchDebounced || undefined },
      });
      setData(res.items || []);
      setTotal(res.total ?? 0);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchDebounced]);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      numeroCliente: row.numeroCliente,
      nombre: row.nombre,
      direccion: row.direccion || '',
      localidad: row.localidad || '',
      cuit: row.cuit || '',
      adminNombre: row.adminNombre || '',
      adminTelefono: row.adminTelefono || '',
      adminMail: row.adminMail || '',
    });
    setModalOpen(true);
  };

  const submit = async () => {
    try {
      const v = await form.validateFields();
      if (editing) {
        await api.put(`/clientes/${editing.numeroCliente}`, {
          nombre: v.nombre,
          direccion: v.direccion || null,
          localidad: v.localidad || null,
          cuit: v.cuit || null,
          adminNombre: v.adminNombre?.trim() || null,
          adminTelefono: v.adminTelefono?.trim() || null,
          adminMail: v.adminMail?.trim() || null,
        });
        message.success('Cliente actualizado');
      } else {
        await api.post('/clientes', {
          numeroCliente: Number(v.numeroCliente),
          nombre: v.nombre,
          direccion: v.direccion || null,
          localidad: v.localidad || null,
          cuit: v.cuit || null,
          adminNombre: v.adminNombre?.trim() || null,
          adminTelefono: v.adminTelefono?.trim() || null,
          adminMail: v.adminMail?.trim() || null,
        });
        message.success('Cliente creado');
      }
      setModalOpen(false);
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'Error al guardar');
    }
  };

  const handleDelete = (row) => {
    Modal.confirm({
      title: 'Eliminar cliente',
      content: `¿Eliminar cliente ${row.numeroCliente} — ${row.nombre}?`,
      okText: 'Eliminar',
      okType: 'danger',
      onOk: async () => {
        try {
          await api.delete(`/clientes/${row.numeroCliente}`);
          message.success('Cliente eliminado');
          load();
        } catch (e) {
          message.error(e?.response?.data?.message || 'Error al eliminar');
        }
      },
    });
  };

  const toggleActiva = useCallback(
    async (row, checked) => {
      try {
        await api.patch(`/clientes/${row.numeroCliente}/activa`, { activa: checked });
        message.success(checked ? 'Cliente activado' : 'Cliente desactivado');
        load();
      } catch (e) {
        message.error(e?.response?.data?.message || 'No se pudo cambiar el estado');
      }
    },
    [load]
  );

  const columns = [
    { title: 'Nº', dataIndex: 'numeroCliente', width: 72, sorter: false, fixed: isNarrow ? 'left' : undefined },
    { title: 'Nombre', dataIndex: 'nombre', ellipsis: true },
    {
      title: 'Direccion',
      dataIndex: 'direccion',
      ellipsis: true,
      responsive: ['md'],
    },
    {
      title: 'Localidad',
      dataIndex: 'localidad',
      width: 160,
      ellipsis: true,
      responsive: ['md'],
    },
    {
      title: 'CUIT',
      dataIndex: 'cuit',
      width: 130,
      responsive: ['lg'],
    },
    {
      title: 'Adm. / fact.',
      dataIndex: 'adminNombre',
      ellipsis: true,
      width: 140,
      responsive: ['lg'],
      render: (v, row) => v || row.adminMail || '—',
    },
    {
      title: 'Activo',
      dataIndex: 'activa',
      width: 100,
      align: 'center',
      render: (v, row) => (
        <Switch
          size="small"
          checked={v !== false}
          checkedChildren="Si"
          unCheckedChildren="No"
          onChange={(checked) => void toggleActiva(row, checked)}
        />
      ),
    },
    {
      title: '',
      key: 'acciones',
      width: 88,
      fixed: isNarrow ? 'right' : undefined,
      render: (_, row) => (
        <Space size="small" wrap>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row)} />
        </Space>
      ),
    },
  ];

  return (
    <Card
      style={{ background: '#ffffff', borderColor: '#d8e8dc', maxWidth: '100%' }}
      styles={{ body: { padding: isNarrow ? 12 : 16 } }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: isNarrow ? 'column' : 'row',
            alignItems: isNarrow ? 'stretch' : 'center',
            justifyContent: 'space-between',
            gap: 12,
            width: '100%',
          }}
        >
          <Title level={5} style={{ margin: 0, color: '#198039' }}>
            Clientes
          </Title>
          <Space
            direction={isNarrow ? 'vertical' : 'horizontal'}
            style={{ width: isNarrow ? '100%' : 'auto' }}
            size="small"
          >
            <Input.Search
              allowClear
              placeholder="Buscar..."
              style={{ width: isNarrow ? '100%' : 220 }}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} block={isNarrow}>
              Nuevo
            </Button>
          </Space>
        </div>

        <Table
          rowKey="numeroCliente"
          loading={loading}
          columns={columns}
          dataSource={data}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: false,
            onChange: (p) => setPage(p),
            showTotal: (t) => `${t} registros`,
            size: isNarrow ? 'small' : 'default',
            simple: isNarrow,
          }}
          scroll={{ x: isNarrow ? 520 : 1140 }}
          size="small"
          onRow={(row) =>
            row.activa === false ? { style: { background: '#f3f4f6' } } : undefined
          }
        />
      </Space>

      <Modal
        title={editing ? 'Editar cliente' : 'Nuevo cliente'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        destroyOnClose
        okText="Guardar"
        width={isNarrow ? 'calc(100vw - 16px)' : 560}
        centered
        styles={{ body: { maxHeight: 'min(70vh, 520px)', overflowY: 'auto' } }}
      >
        <Form form={form} layout="vertical" requiredMark={false} style={{ marginTop: 8 }}>
          <Form.Item
            name="numeroCliente"
            label="Numero de cliente"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Input type="number" disabled={!!editing} placeholder="Ej: 292" />
          </Form.Item>
          <Form.Item name="nombre" label="Nombre" rules={[{ required: true, message: 'Requerido' }]}>
            <Input placeholder="Razon social / nombre" />
          </Form.Item>
          <Form.Item name="direccion" label="Direccion">
            <Input.TextArea rows={2} placeholder="Calle y numero" />
          </Form.Item>
          <Form.Item name="localidad" label="Localidad">
            <Input placeholder="Ciudad / localidad" />
          </Form.Item>
          <Form.Item name="cuit" label="CUIT">
            <Input placeholder="XX-XXXXXXXX-X" />
          </Form.Item>
          <Divider orientation="left" plain style={{ margin: '16px 0 12px', color: '#198039', borderColor: '#d8e8dc' }}>
            Contacto administración y facturación
          </Divider>
          <Form.Item name="adminNombre" label="Nombre">
            <Input placeholder="Nombre del contacto" />
          </Form.Item>
          <Form.Item name="adminTelefono" label="Teléfono">
            <Input placeholder="Teléfono" />
          </Form.Item>
          <Form.Item name="adminMail" label="Mail">
            <Input type="email" placeholder="correo@ejemplo.com" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Clientes;
